import { qoursesApi } from '@/api/qourses.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popModal } from '@/components/modals/index.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { Input } from '@/shadcn/components/ui/input.tsx'
import { Label } from '@/shadcn/components/ui/label.tsx'
import { Progress } from '@/shadcn/components/ui/progress.tsx'
import { ToastVariant, minDelay, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileSpreadsheet,
  Globe,
  Loader2,
  Mail,
  SkipForward,
  Upload,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'

interface ParsedLead {
  email: string
  websiteUrl?: string
  title?: string
  placeUrl?: string
  subtitle?: string
  rating?: string
  reviewCount?: string
  category?: string
  address?: string
  phoneNumber?: string
  instagramUrl?: string
  originalRow: number
}

interface ProcessedLead extends ParsedLead {
  status: 'pending' | 'approved' | 'skipped' | 'error'
  errorMessage?: string
  visitedWebsite?: boolean
  visitedInstagram?: boolean
}

const genericEmailProviders = [
  // International providers
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'protonmail.com',
  'yandex.com',
  'mail.com',
  'zoho.com',
  'fastmail.com',
  'live.com',
  'msn.com',
  'me.com',
  'mac.com',
  // German providers
  'gmx.de',
  'gmx.net',
  'web.de',
  't-online.de',
  'freenet.de',
  '1und1.de',
  '1&1.de',
  'arcor.de',
  'alice.de',
  'vodafone.de',
  'telekom.de',
  'bluewin.ch',
  'sunrise.ch',
  'swissonline.ch',
  // European providers
  'yahoo.de',
  'hotmail.de',
  'outlook.de',
  'posteo.de',
  'mailbox.org',
  'tutanota.com',
  'tutanota.de',
  // Austrian providers
  'gmx.at',
  'yahoo.at',
  'hotmail.at',
  'outlook.at',
  'chello.at',
  'aon.at',
  // Swiss providers
  'hispeed.ch',
  'vtx.ch',
  'green.ch',
]

export default function BulkCreateLeadsModal() {
  const queryClient = useQueryClient()
  const [stage, setStage] = useState<'upload' | 'review'>('upload')
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [parsedLeads, setParsedLeads] = useState<ProcessedLead[]>([])
  const [currentLeadIndex, setCurrentLeadIndex] = useState(0)
  const [isProcessing, setIsProcessing] = useState(false)
  const [editedEmail, setEditedEmail] = useState('')
  const [editedWebsite, setEditedWebsite] = useState('')

  const currentLead = parsedLeads[currentLeadIndex]
  const approvedCount = parsedLeads.filter((l) => l.status === 'approved').length
  const skippedCount = parsedLeads.filter((l) => l.status === 'skipped').length
  const errorCount = parsedLeads.filter((l) => l.status === 'error').length
  const progressPercentage = ((currentLeadIndex + 1) / parsedLeads.length) * 100

  // Parse CSV file with proper quote handling
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      const nextChar = line[i + 1]

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"'
          i++ // Skip next quote
        } else {
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    result.push(current.trim())
    return result
  }

  const parseCSV = (file: File): Promise<ParsedLead[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string
          const lines = text.split('\n').filter((line) => line.trim())
          const leads: ParsedLead[] = []

          if (lines.length === 0) {
            reject(new Error('CSV file is empty'))
            return
          }

          // Parse header row
          const headers = parseCSVLine(lines[0]).map((h) => h.toLowerCase().trim())

          // Create column index map
          const columnMap: Record<string, number> = {}
          headers.forEach((header, index) => {
            columnMap[header] = index
          })

          // Parse data rows (skip header)
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim()
            if (!line) continue

            const values = parseCSVLine(line)

            // Helper to safely get column value
            const getColumn = (name: string): string => {
              const index = columnMap[name]
              if (index === undefined || index >= values.length) return ''
              const value = values[index]
              return value ? value.trim() : ''
            }

            const email = getColumn('email').toLowerCase()
            const websiteUrl = getColumn('website')
            const title = getColumn('title')
            const placeUrl = getColumn('placeurl')
            const subtitle = getColumn('subtitle')
            const rating = getColumn('rating')
            const reviewCount = getColumn('reviewcount')
            const category = getColumn('category')
            const address = getColumn('address')
            const phoneNumber = getColumn('phonenumber')
            const instagramUrl = getColumn('instagramurl')

            // Only process rows with valid email
            if (!email || !email.includes('@')) {
              continue
            }

            // Use website or placeUrl (prefer website)
            let finalWebsiteUrl = websiteUrl || placeUrl

            // Normalize website URL
            if (finalWebsiteUrl && !finalWebsiteUrl.startsWith('http')) {
              finalWebsiteUrl = `https://${finalWebsiteUrl}`
            }

            // If no website but email has non-generic domain, auto-generate website
            if (!finalWebsiteUrl) {
              const domain = email.split('@')[1]
              if (domain && !genericEmailProviders.includes(domain)) {
                finalWebsiteUrl = `https://${domain}`
              }
            }

            leads.push({
              email,
              websiteUrl: finalWebsiteUrl || undefined,
              title: title || undefined,
              placeUrl: placeUrl || undefined,
              subtitle: subtitle || undefined,
              rating: rating || undefined,
              reviewCount: reviewCount || undefined,
              category: category || undefined,
              address: address || undefined,
              phoneNumber: phoneNumber || undefined,
              instagramUrl: instagramUrl || undefined,
              originalRow: i + 1,
            })
          }

          if (leads.length === 0) {
            reject(new Error('No valid email addresses found in CSV'))
          } else {
            resolve(leads)
          }
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    })
  }

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (!file) return

    if (!file.name.endsWith('.csv')) {
      sendNotification('Invalid File', 'Please upload a CSV file', ToastVariant.Error)
      return
    }

    setCsvFile(file)

    try {
      const leads = await parseCSV(file)
      const processedLeads = leads.map((lead) => ({
        ...lead,
        status: 'pending' as const,
      }))
      setParsedLeads(processedLeads)
      setStage('review')

      // Set initial edited values for first lead
      if (processedLeads.length > 0) {
        setEditedEmail(processedLeads[0].email)
        setEditedWebsite(processedLeads[0].websiteUrl || '')
      }

      sendNotification(
        'CSV Processed',
        `Found ${leads.length} lead${leads.length > 1 ? 's' : ''} to review`,
        ToastVariant.Success,
      )
    } catch (error) {
      sendNotification(
        'Parse Error',
        error instanceof Error ? error.message : 'Failed to parse CSV',
        ToastVariant.Error,
      )
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  })

  // Update edited values when current lead changes
  const updateEditedValues = (index: number) => {
    const lead = parsedLeads[index]
    if (lead) {
      setEditedEmail(lead.email)
      setEditedWebsite(lead.websiteUrl || '')
    }
  }

  // Handle lead approval
  const handleApprove = async () => {
    if (!currentLead) return

    setIsProcessing(true)
    try {
      await minDelay(
        qoursesApi.salesAdmin.salesAdminControllerCreateLeadV1({
          email: editedEmail.trim(),
          websiteUrl: editedWebsite.trim() || undefined,
        }),
        500,
      )

      // Update lead status
      const updatedLeads = [...parsedLeads]
      updatedLeads[currentLeadIndex] = {
        ...currentLead,
        email: editedEmail.trim(),
        websiteUrl: editedWebsite.trim() || undefined,
        status: 'approved',
      }
      setParsedLeads(updatedLeads)

      // Move to next lead
      if (currentLeadIndex < parsedLeads.length - 1) {
        const nextIndex = currentLeadIndex + 1
        setCurrentLeadIndex(nextIndex)
        updateEditedValues(nextIndex)
      } else {
        // All leads processed
        handleComplete()
      }
    } catch (error) {
      // Mark as error and move to next
      const updatedLeads = [...parsedLeads]
      updatedLeads[currentLeadIndex] = {
        ...currentLead,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Failed to create lead',
      }
      setParsedLeads(updatedLeads)

      sendNotification(
        'Creation Failed',
        `Failed to create lead: ${error instanceof Error ? error.message : 'Unknown error'}`,
        ToastVariant.Error,
      )

      if (currentLeadIndex < parsedLeads.length - 1) {
        const nextIndex = currentLeadIndex + 1
        setCurrentLeadIndex(nextIndex)
        updateEditedValues(nextIndex)
      }
    } finally {
      setIsProcessing(false)
    }
  }

  // Handle lead skip
  const handleSkip = () => {
    const updatedLeads = [...parsedLeads]
    updatedLeads[currentLeadIndex] = {
      ...currentLead,
      status: 'skipped',
    }
    setParsedLeads(updatedLeads)

    if (currentLeadIndex < parsedLeads.length - 1) {
      const nextIndex = currentLeadIndex + 1
      setCurrentLeadIndex(nextIndex)
      updateEditedValues(nextIndex)
    } else {
      handleComplete()
    }
  }

  // Navigate to previous lead
  const handlePrevious = () => {
    if (currentLeadIndex > 0) {
      const prevIndex = currentLeadIndex - 1
      setCurrentLeadIndex(prevIndex)
      updateEditedValues(prevIndex)
    }
  }

  // Navigate to next lead
  const handleNext = () => {
    if (currentLeadIndex < parsedLeads.length - 1) {
      const nextIndex = currentLeadIndex + 1
      setCurrentLeadIndex(nextIndex)
      updateEditedValues(nextIndex)
    }
  }

  // Handle completion
  const handleComplete = () => {
    queryClient.invalidateQueries(['admin', 'leads'])

    sendNotification(
      'Bulk Import Complete',
      `Created ${approvedCount} leads, skipped ${skippedCount}${errorCount > 0 ? `, ${errorCount} errors` : ''}`,
      ToastVariant.Success,
      5000,
    )

    popModal()
  }

  // Auto-populate website when email changes
  const handleEmailChange = (value: string) => {
    setEditedEmail(value)

    if (value && value.includes('@') && !editedWebsite) {
      const domain = value.split('@')[1]?.toLowerCase().trim()
      if (domain && !genericEmailProviders.includes(domain)) {
        setEditedWebsite(`https://${domain}`)
      }
    }
  }

  // Open website and mark as visited
  const openWebsite = () => {
    if (!editedWebsite) return

    const url = editedWebsite.startsWith('http') ? editedWebsite : `https://${editedWebsite}`
    window.open(url, '_blank', 'noopener,noreferrer')

    // Mark as visited
    const updatedLeads = [...parsedLeads]
    updatedLeads[currentLeadIndex] = {
      ...currentLead,
      visitedWebsite: true,
    }
    setParsedLeads(updatedLeads)
  }

  // Open Instagram and mark as visited
  const openInstagram = () => {
    if (!currentLead?.instagramUrl) return

    window.open(currentLead.instagramUrl, '_blank', 'noopener,noreferrer')

    // Mark as visited
    const updatedLeads = [...parsedLeads]
    updatedLeads[currentLeadIndex] = {
      ...currentLead,
      visitedInstagram: true,
    }
    setParsedLeads(updatedLeads)
  }

  // Keyboard shortcuts
  useEffect(() => {
    if (stage !== 'review' || !currentLead) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input field
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return

      const key = e.key.toLowerCase()

      // Handle shortcuts
      switch (key) {
        case 'a':
          if (currentLeadIndex > 0 && !isProcessing) {
            e.preventDefault()
            handlePrevious()
          }
          break
        case 'd':
          if (currentLeadIndex < parsedLeads.length - 1 && !isProcessing) {
            e.preventDefault()
            handleNext()
          }
          break
        case ' ':
          if (currentLead.status === 'pending' && !isProcessing) {
            e.preventDefault()
            handleSkip()
          }
          break
        case 'enter':
          if (currentLead.status === 'pending' && !isProcessing && editedEmail) {
            e.preventDefault()
            e.stopPropagation()
            handleApprove()
          }
          break
        case 'w':
          if (editedWebsite) {
            e.preventDefault()
            openWebsite()
          }
          break
        case 'i':
          if (currentLead?.instagramUrl) {
            e.preventDefault()
            openInstagram()
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [stage, currentLead, currentLeadIndex, isProcessing, editedEmail, parsedLeads.length])

  return (
    <Dynamic.Content className="p-6 sm:p-8 max-w-4xl">
      <DialogHeader className="mb-4">
        <DialogTitle className="mt-6 sm:mt-2">
          {stage === 'upload'
            ? 'Bulk Import Leads'
            : `Review Leads (${currentLeadIndex + 1}/${parsedLeads.length})`}
        </DialogTitle>
        <DialogDescription>
          {stage === 'upload'
            ? 'Upload a CSV file containing email addresses and optional website URLs'
            : 'Shortcuts: A/D navigate, Space skip, Enter approve, W open website, I open Instagram.'}
        </DialogDescription>
      </DialogHeader>

      {stage === 'upload' ? (
        <div className="space-y-4">
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors duration-200 
              ${
                isDragActive
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              }
            `}
          >
            <input {...getInputProps()} />
            <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            {isDragActive ? (
              <p className="text-indigo-600 font-medium">Drop the CSV file here...</p>
            ) : (
              <>
                <p className="text-gray-900 font-medium mb-2">
                  Drag and drop a CSV file here, or click to select
                </p>
                <p className="text-sm text-gray-500">
                  CSV should contain email addresses, and optionally website URLs
                </p>
              </>
            )}
          </div>

          {csvFile && (
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-green-900">{csvFile.name}</span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setCsvFile(null)
                  setParsedLeads([])
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">CSV Format Tips:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Include column headers: title, placeUrl, website, email, etc.</li>
                  <li>Email column is required, other columns are optional</li>
                  <li>
                    Supported columns: title, subtitle, category, rating, reviewCount, address,
                    phoneNumber, instagramUrl
                  </li>
                  <li>Website URLs will be auto-detected from website or placeUrl columns</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>
                Progress: {currentLeadIndex + 1} of {parsedLeads.length}
              </span>
              <span>
                {approvedCount > 0 && (
                  <span className="text-green-600 mr-3">✓ {approvedCount}</span>
                )}
                {skippedCount > 0 && <span className="text-yellow-600 mr-3">⊘ {skippedCount}</span>}
                {errorCount > 0 && <span className="text-red-600">✗ {errorCount}</span>}
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>

          {/* Current lead details */}
          {currentLead && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-500">
                  Row {currentLead.originalRow} from CSV
                </span>
                {currentLead.status !== 'pending' && (
                  <span
                    className={`
                    px-2 py-1 text-xs font-medium rounded-full
                    ${currentLead.status === 'approved' ? 'bg-green-100 text-green-800' : ''}
                    ${currentLead.status === 'skipped' ? 'bg-yellow-100 text-yellow-800' : ''}
                    ${currentLead.status === 'error' ? 'bg-red-100 text-red-800' : ''}
                  `}
                  >
                    {currentLead.status.toUpperCase()}
                  </span>
                )}
              </div>

              {/* Lead metadata (read-only info) */}
              {(currentLead.title || currentLead.category || currentLead.rating) && (
                <div className="space-y-2 pb-3 border-b border-gray-200">
                  {currentLead.title && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">
                        Title:
                      </span>
                      <span className="text-sm text-gray-900">{currentLead.title}</span>
                    </div>
                  )}
                  {currentLead.subtitle && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">
                        Subtitle:
                      </span>
                      <span className="text-sm text-gray-900">{currentLead.subtitle}</span>
                    </div>
                  )}
                  {currentLead.category && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">
                        Category:
                      </span>
                      <span className="text-sm text-gray-900">{currentLead.category}</span>
                    </div>
                  )}
                  {(currentLead.rating || currentLead.reviewCount) && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">
                        Rating:
                      </span>
                      <span className="text-sm text-gray-900">
                        {currentLead.rating && `${currentLead.rating} ⭐`}
                        {currentLead.reviewCount && ` (${currentLead.reviewCount} reviews)`}
                      </span>
                    </div>
                  )}
                  {currentLead.address && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">
                        Address:
                      </span>
                      <span className="text-sm text-gray-900">{currentLead.address}</span>
                    </div>
                  )}
                  {currentLead.phoneNumber && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">
                        Phone:
                      </span>
                      <span className="text-sm text-gray-900">{currentLead.phoneNumber}</span>
                    </div>
                  )}
                  {currentLead.instagramUrl && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">
                        Instagram:
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={openInstagram}
                          className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
                        >
                          {currentLead.instagramUrl}
                        </button>
                        {currentLead.visitedInstagram && (
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <Label htmlFor="review-email" className="flex items-center gap-2 mb-1">
                    <Mail className="h-4 w-4" />
                    Email Address
                  </Label>
                  <Input
                    id="review-email"
                    className="bg-white"
                    type="email"
                    value={editedEmail}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    disabled={isProcessing || currentLead.status !== 'pending'}
                  />
                </div>

                <div>
                  <Label htmlFor="review-website" className="flex items-center gap-2 mb-1">
                    <Globe className="h-4 w-4" />
                    Website URL
                  </Label>
                  <Input
                    id="review-website"
                    type="url"
                    className="bg-white"
                    value={editedWebsite}
                    onChange={(e) => setEditedWebsite(e.target.value)}
                    disabled={isProcessing || currentLead.status !== 'pending'}
                    placeholder="https://example.com"
                  />
                  {editedWebsite && (
                    <button
                      onClick={openWebsite}
                      className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Open website in new tab (W)
                      {currentLead?.visitedWebsite && (
                        <CheckCircle className="h-3 w-3 text-green-600 ml-1" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {currentLead.status === 'error' && currentLead.errorMessage && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                  {currentLead.errorMessage}
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-between">
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                disabled={currentLeadIndex === 0 || isProcessing}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
                <kbd className="ml-2 px-1.5 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">
                  A
                </kbd>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                disabled={currentLeadIndex === parsedLeads.length - 1 || isProcessing}
              >
                Next
                <kbd className="ml-2 px-1.5 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">
                  D
                </kbd>
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {currentLead?.status === 'pending' ? (
              <div className="flex space-x-2">
                <Button variant="secondary" onClick={handleSkip} disabled={isProcessing}>
                  <SkipForward className="h-4 w-4 mr-2" />
                  Skip
                  <kbd className="ml-2 px-1.5 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">
                    Space
                  </kbd>
                </Button>
                <Button
                  variant="indigo"
                  onClick={handleApprove}
                  disabled={isProcessing || !editedEmail}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve & Create
                      <kbd className="ml-2 px-1.5 py-0.5 text-xs font-semibold text-white bg-indigo-700 border border-indigo-600 rounded">
                        Enter
                      </kbd>
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">
                Already processed - use navigation to review
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer buttons */}
      <div className="mt-6 flex justify-end space-x-2">
        <Button variant="outline" onClick={() => popModal()}>
          {stage === 'upload' ? 'Cancel' : 'Close'}
        </Button>
        {stage === 'review' && currentLeadIndex === parsedLeads.length - 1 && (
          <Button variant="indigo" onClick={handleComplete}>
            <Upload className="h-4 w-4 mr-2" />
            Complete Import
          </Button>
        )}
      </div>
    </Dynamic.Content>
  )
}
